import { Divider } from "@affinity/components/Divider";
import ItemCard from "@affinity/components/ItemCard/ItemCard";
import { CardGridContainer } from "@affinity/components/ProductList/ProductListCardGrid";
import { Section } from "@affinity/components/Section";
import GetQuoteInitialise from "@affinity/Handlers/GetQuoteInitialise";
import SetQuoteBasicDetails, { SetQuoteBasicDetailsParameters } from "@affinity/Handlers/SetQuoteBasicDetails";
import { CustomState } from "@affinity/Store/Reducers";
import { AffinityProduct } from "@affinity/Store/StoreTypes";
import Zone from "@insite/client-framework/Components/Zone";
import { getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    const { value } = getPageStateByType(state, "AffinityGetQuotePage");
    const regularId = value?.fields.regularId;
    const premiumId = value?.fields.premiumId;
    const productRegular = state.affinity_get_quote.productRegular;
    const productPremium = state.affinity_get_quote.productPremium;

    // Map required state elements to component properties
    return {
        regularId,
        premiumId,
        productRegular,
        productPremium,
        formState: state.affinity_get_quote,
        isInitialised: state.affinity_get_quote.isInitialised,
        token: state.affinity_server_side_rendering.SecurityToken,
    };
};

const mapDispatchToProps = {
    // Map loader and setter methods to component properties
    GetQuoteInitialise,
    SetQuoteBasicDetails,
};

type Step1BasicDetailsProps = {
    contentId: string;
};
type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & Step1BasicDetailsProps;

export const Step1BasicDetails: React.FC<Props> = ({
    SetQuoteBasicDetails,
    GetQuoteInitialise,
    contentId,
    formState,
    premiumId,
    regularId,
    productRegular,
    productPremium,
    isInitialised,
    token,
}) => {
    if (!isInitialised) {
        GetQuoteInitialise({
            productRegularId: regularId,
            productPremiumId: premiumId,
        });
    }
    const [formData, setFormData] = useState({
        selectedProduct: formState.selectedProduct ?? AffinityProduct.Regular,
        postcode: formState.postcode ?? "",
        emailAddress: formState.emailAddress ?? "",
        allowMarketing: formState.allowMarketing ?? false,
    });

    function submit(data: SetQuoteBasicDetailsParameters) {
        SetQuoteBasicDetails(data);
    }
    const handleSubmit = () => {
        submit({ ...formData, token: token ?? "" });
    };

    return (
        <form onSubmit={handleSubmit}>
            <Section>
                <Typography variant="h3">Select fuel type</Typography>
                <RadioGroup
                    value={formData.selectedProduct.toString()}
                    name="selectedProduct"
                    onChangeHandler={e => {
                        const newFormData = {
                            ...formData,
                            selectedProduct: e.target.value === "1" ? AffinityProduct.Regular : AffinityProduct.Premium,
                        };
                        setFormData(newFormData);
                    }}
                    css={css`
                        legend {
                            display: none;
                        }
                    `}
                >
                    <CardGridContainer>
                        <ItemCard.Container>
                            <Radio value={AffinityProduct.Regular.toString()}>
                                <ItemCard.Title>{productRegular?.productTitle}</ItemCard.Title>
                            </Radio>
                        </ItemCard.Container>
                        <ItemCard.Container>
                            <Radio value={AffinityProduct.Premium.toString()}>
                                <ItemCard.Title>{productPremium?.productTitle}</ItemCard.Title>
                            </Radio>
                        </ItemCard.Container>
                    </CardGridContainer>
                </RadioGroup>
                <Zone contentId={contentId} zoneName="Step1Info" requireRows={false} />
                <TextField
                    label="Delivery Postcode"
                    type="text"
                    required={true}
                    autoComplete="true"
                    name="postcode"
                    pattern="^[A-Za-z]{1,2}[0-9][0-9A-Za-z]?\s?[0-9][A-Za-z]{2}$"
                    value={formData.postcode}
                    onChange={e => {
                        const newFormData = {
                            ...formData,
                            postcode: e.target.value,
                        };
                        setFormData(newFormData);
                    }}
                />
                <TextField
                    label="Email address"
                    type="email"
                    required={true}
                    autoComplete="true"
                    pattern=".+@.*?\..{1,}"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={e => {
                        const newFormData = {
                            ...formData,
                            emailAddress: e.target.value,
                        };
                        setFormData(newFormData);
                    }}
                />
                <Checkbox
                    checked={!!formData.allowMarketing}
                    onChange={e => {
                        const newFormData = {
                            ...formData,
                            allowMarketing: !formData.allowMarketing,
                        };
                        setFormData(newFormData);
                    }}
                >
                    {translate("Check this box if you'd like to receive marketing messages from Affinity Fuels.")}
                </Checkbox>
                <Divider />
                <Button
                    type="submit"
                    variant="primary"
                    sizeVariant="large"
                    icon={{ position: "right", src: "ChevronRight" }}
                    css={css`
                        align-self: end;
                    `}
                    disabled={!formData?.postcode || !formData?.emailAddress}
                >
                    {translate("Continue")}
                </Button>
            </Section>
        </form>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Step1BasicDetails);
